import { api } from "./api";
import { isTauriSync } from "./tauri";
import { isCapacitor } from "./capacitor";
import { disableFcmNotifications, enableFcmNotifications } from "./fcm";

/** Hex-string van Uint8Array → urlBase64 voor PushManager.subscribe. */
function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function arrayBufferToBase64(buf: ArrayBuffer | null): string {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  // Strip padding voor compactere opslag; server verwacht raw urlsafe of
  // standaard b64 — wij gebruiken standaard b64 met padding.
  return btoa(bin);
}

/** Vraagt de push-permissie aan vanuit een user-gesture en registreert de
 *  subscription bij de server. Geen-op in Tauri-mode of als de browser
 *  geen Push ondersteunt. Returntype geeft aan of het uiteindelijk
 *  geslaagd is — caller kan een toast tonen. */
export async function enablePushNotifications(): Promise<
  { ok: true } | { ok: false; reason: string }
> {
  if (isTauriSync()) return { ok: false, reason: "tauri" };
  // Capacitor-Android: niet de Web-Push-flow maar FCM via de Capacitor-
  // plugin. Maakt het "🔔 meldingen aanzetten"-pad in StatusBar één
  // entry-point, ongeacht het platform.
  if (isCapacitor()) return enableFcmNotifications();
  if (typeof window === "undefined") return { ok: false, reason: "no_window" };
  if (!("serviceWorker" in navigator)) return { ok: false, reason: "no_sw" };
  if (!("PushManager" in window)) return { ok: false, reason: "no_push" };
  if (!("Notification" in window)) return { ok: false, reason: "no_notif" };

  // Permissie moet vanaf een user-gesture; caller bindt deze functie aan
  // een click-handler.
  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return { ok: false, reason: "denied" };

  let registration: ServiceWorkerRegistration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch {
    return { ok: false, reason: "sw_not_ready" };
  }

  // Bestaande subscription hergebruiken (zelfde browser, ander gesprek).
  let sub = await registration.pushManager.getSubscription();
  if (!sub) {
    let publicKey: string;
    try {
      const r = await api.getPushPublicKey();
      publicKey = r.vapid_public_key;
    } catch {
      return { ok: false, reason: "no_vapid" };
    }
    try {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (err) {
      console.error("[push] subscribe failed:", err);
      return { ok: false, reason: "subscribe_failed" };
    }
  }

  const keys = (sub.toJSON() as { keys?: { p256dh?: string; auth?: string } }).keys ?? {};
  const p256dh = keys.p256dh ?? arrayBufferToBase64(sub.getKey("p256dh"));
  const auth = keys.auth ?? arrayBufferToBase64(sub.getKey("auth"));
  if (!p256dh || !auth) return { ok: false, reason: "no_keys" };

  try {
    await api.subscribePush({
      endpoint: sub.endpoint,
      p256dh,
      auth_key: auth,
      device_label: detectDeviceLabel(),
    });
  } catch (err) {
    console.error("[push] server subscribe failed:", err);
    return { ok: false, reason: "server_failed" };
  }

  // Periodic Background Sync — werkt momenteel alleen op Chromium met
  // geïnstalleerde PWA + voldoende engagement-score. Op iOS Safari een
  // no-op. Frequentie wordt door de browser bepaald (niet exact 5 min);
  // we vragen minimaal 5 min, in praktijk komt 'm tussen 30 min en
  // enkele uren langs. Beste-effort, geen probleem als 'ie weigert.
  try {
    const swr = registration as ServiceWorkerRegistration & {
      periodicSync?: { register(tag: string, opts: { minInterval: number }): Promise<void> };
    };
    if (swr.periodicSync) {
      // Permissie check (Chrome heeft 'periodic-background-sync').
      const nav = navigator as Navigator & {
        permissions?: {
          query(d: { name: string }): Promise<{ state: string }>;
        };
      };
      const perm = await nav.permissions
        ?.query({ name: "periodic-background-sync" })
        .catch(() => null);
      if (!perm || perm.state === "granted") {
        await swr.periodicSync.register("swift-presence", {
          minInterval: 5 * 60 * 1000,
        });
      }
    }
  } catch {
    /* periodic sync niet ondersteund — niet erg */
  }
  return { ok: true };
}

export async function disablePushNotifications(): Promise<void> {
  if (isTauriSync()) return;
  if (isCapacitor()) {
    await disableFcmNotifications();
    return;
  }
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  try {
    await api.unsubscribePush(sub.endpoint);
  } catch {
    /* */
  }
  try {
    await sub.unsubscribe();
  } catch {
    /* */
  }
}

/** Heuristisch device-label voor de Sessies-lijst. */
export function detectDeviceLabel(): string {
  if (typeof navigator === "undefined") return "PWA";
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return "iPhone (Safari)";
  if (/iPad/.test(ua)) return "iPad (Safari)";
  if (/Android/.test(ua)) {
    if (/Chrome/.test(ua)) return "Android (Chrome)";
    return "Android";
  }
  if (/Windows/.test(ua)) return "Windows (browser)";
  if (/Mac OS X/.test(ua)) return "Mac (browser)";
  if (/Linux/.test(ua)) return "Linux (browser)";
  return "PWA";
}

/** True als de PWA als geïnstalleerde standalone-app draait (van home
 *  screen op iOS, of na "installeren" op desktop/Android). */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) {
    return true;
  }
  // iOS-eigen detector (Safari, niet standaard).
  const navWithStandalone = navigator as Navigator & { standalone?: boolean };
  return navWithStandalone.standalone === true;
}

/** Stuurt een presence-ping naar de server. Stille fail bij fouten —
 *  het is een best-effort heartbeat, niet kritiek. */
async function sendPresencePing(): Promise<void> {
  try {
    await api.presencePing();
  } catch {
    /* */
  }
}

/** Plant visibility-based presence-pings: zodra de PWA visible wordt
 *  (tab-switch, app uit recents geopend, scherm aan), één ping. Bonus
 *  voor situaties waar push net niet werkt. Geen-op in Tauri-mode. */
export function installVisibilityPing(): () => void {
  if (isTauriSync()) return () => undefined;
  if (typeof document === "undefined") return () => undefined;
  const onVis = () => {
    if (document.visibilityState === "visible") void sendPresencePing();
  };
  document.addEventListener("visibilitychange", onVis);
  // Bij eerste mount ook één keer (vooral: SW heeft 'm net gewekt, of
  // gebruiker keert terug uit een diepe achtergrond).
  void sendPresencePing();
  return () => document.removeEventListener("visibilitychange", onVis);
}

/** Luistert naar berichten vanuit de Service Worker — bv. een
 *  re-subscribe-verzoek na pushsubscriptionchange, of een open-
 *  conversation-deeplink na een notification-click. */
export function installSWMessageHandler(args: {
  onOpenConversation: (id: number) => void;
}): () => void {
  if (isTauriSync()) return () => undefined;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return () => undefined;
  }
  const handler = (event: MessageEvent) => {
    const data = event.data as
      | { type: "swift:open-conversation"; conversation_id?: number }
      | { type: "swift:resubscribe-push" }
      | undefined;
    if (!data || typeof data !== "object") return;
    if (data.type === "swift:open-conversation" && data.conversation_id) {
      args.onOpenConversation(data.conversation_id);
    } else if (data.type === "swift:resubscribe-push") {
      void enablePushNotifications();
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}
